import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./NotesPreviewCell.css";

const EMPTY = "—";

/**
 * Single-line truncated notes. When truncated, the text itself is a link;
 * click opens a modal with the full notes.
 */
export default function NotesPreviewCell({
  notes,
  title = "Notes",
  className = "",
}) {
  const textRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState(false);

  const raw = notes == null ? "" : String(notes).trim();
  const isEmpty = !raw || raw === EMPTY;
  const display = isEmpty ? EMPTY : raw.replace(/\s+/g, " ");
  // Length fallback so long notes stay clickable even if overflow measure lags
  const likelyTruncated = !isEmpty && (display.length > 28 || raw.includes("\n"));
  const interactive = !isEmpty && (truncated || likelyTruncated);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || isEmpty) {
      setTruncated(false);
      return undefined;
    }
    const check = () => {
      // Compare against parent width too — button min-content can fool scrollWidth
      const parent = el.parentElement;
      const limit = parent ? parent.clientWidth : el.clientWidth;
      setTruncated(el.scrollWidth > limit + 1 || el.scrollWidth > el.clientWidth + 1);
    };
    check();
    const raf = requestAnimationFrame(check);
    if (typeof ResizeObserver === "undefined") {
      return () => cancelAnimationFrame(raf);
    }
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [raw, isEmpty, display]);

  const openModal = (e) => {
    if (!interactive) return;
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setOpen(true);
  };

  const closeModal = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setOpen(false);
  };

  return (
    <>
      <div
        className={`notes-preview-cell${isEmpty ? " is-empty" : ""}${interactive ? " is-interactive" : ""}${className ? ` ${className}` : ""}`}
      >
        {isEmpty ? (
          <span className="notes-preview-text">{EMPTY}</span>
        ) : (
          <button
            type="button"
            ref={textRef}
            className={`notes-preview-text${interactive ? " notes-preview-link" : ""}`}
            onClick={openModal}
            title={interactive ? "View full notes" : undefined}
          >
            {display}
          </button>
        )}
      </div>
      {open &&
        createPortal(
          <div className="notes-preview-modal-overlay" onClick={closeModal}>
            <div
              className="notes-preview-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="notes-preview-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="notes-preview-modal-header">
                <h3 id="notes-preview-modal-title">{title}</h3>
                <button
                  type="button"
                  className="notes-preview-modal-close"
                  onClick={closeModal}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <div className="notes-preview-modal-body">{raw}</div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
